import React from "react";
import { LeadCard, InlineAdd } from "./parts";
import { churnView } from "./leadModel.mjs";
import { wouldScore } from "../scoreView.mjs";

/*
 * CHURNED: when, why, how long they were a client, what they paid, and the churn
 * notice (frame 1i, "Brightwave · churned lead block" and "An older churn, fields
 * missing"). Read-only: no sales actions anywhere on a churned record.
 *
 * Built from what Mark churned… stores (record/endings.mjs writesFor "churn"):
 * churned_at is the end of service, the notice is its timeline note ("Churn
 * notice": "Notice received Jul 31, 2026. Service ends …"), and the reason is
 * status_reason. The cleanup's "not in auditor roster" and a bare "churned" are not
 * reasons and read "reason not recorded" (reality.txt [18]). No sender and no email
 * are linked ("from their CMO", Open in Gmail): nothing stores which message was the
 * notice, and guessing could quote the wrong one.
 *
 * TWO FORMS, AS DRAWN. With the notice note (every churn Mark churned… wrote), the
 * full block: the headline, the notice line, Client and Signed fee, the notice
 * quoted. Without it (an older churn, marked some other way), the compact grid:
 * Churned, Why, Signed fee, Notice, each "not recorded" where empty, with Add on
 * the two a person can know. The notice is never worked back from churned_at there:
 * only Mark churned… sets churned_at to notice + 30 days (leadModel.churnView).
 *
 * Props:
 *   record   the shell's bundle: company (churned_at, won_at, status_reason,
 *            signed_fee_structure), timeline.rows (the Churn notice note), score
 *   ops      ops.patchCompany(body) (the reason's and the fee's Add)
 */
export default function Churned({ record, ops }) {
  const v = churnView(record.company, record.timeline.rows, { now: record.now });
  const would = wouldScore(record.score);
  const meta = would ? would.charAt(0).toLowerCase() + would.slice(1) : null;
  const addReason = <InlineAdd placeholder="Why they left, in a few words" onSave={(text) => ops.patchCompany({ status_reason: text })} />;
  const addFee = <InlineAdd multiline placeholder={"$4,500/mo base\n12% of spend above $25K"} hint="One line per part, as the agreement words it." onSave={(text) => ops.patchCompany({ signed_fee_structure: text })} />;

  if (!v.complete) {
    return (
      <section className="rec-card rec-card--tight rec-lead rec-lead--grid" aria-label="Churned">
        <div className="rec-kv">
          <span>Churned</span>
          <span>{v.churnedOn || "date not recorded"}</span>
          {v.tenure && (
            <>
              <span>Client</span>
              <span>{v.tenure}</span>
            </>
          )}
          <span>Why</span>
          {v.why ? <span>{v.why}</span> : <span className="rec-sub">reason not recorded · {addReason}</span>}
          <span>Signed fee</span>
          {v.fee.length ? <span>{v.fee.join(" · ")}</span> : <span className="rec-sub">fee not recorded · {addFee}</span>}
          <span>Notice</span>
          <span className="rec-sub">not recorded</span>
          {meta && (
            <>
              <span>Score</span>
              <span className="rec-sub">{meta}</span>
            </>
          )}
        </div>
      </section>
    );
  }

  return (
    <LeadCard label="Churned" meta={meta}>
      <div>
        <div className="rec-headline">{v.headline}</div>
        <div className="rec-body">{v.sub}</div>
      </div>
      <div className="rec-kv rec-sep">
        <span>Client</span>
        <span className={v.tenure ? "" : "rec-sub"}>{v.tenure || "not recorded"}</span>
        {!v.why && (
          <>
            <span>Why</span>
            <span className="rec-sub">reason not recorded · {addReason}</span>
          </>
        )}
        <span>Signed fee</span>
        {v.fee.length ? <span>{v.fee.join(" · ")}</span> : <span className="rec-sub">fee not recorded · {addFee}</span>}
      </div>
      {v.notice && v.notice.body && (
        <div className="rec-lead__sec">
          <span className="rec-lead__small">The churn notice{v.notice.day ? ` · ${v.notice.day}` : ""} · read-only</span>
          <div className="rec-quote rec-quote--md rec-lead__muted">{v.notice.body}</div>
        </div>
      )}
    </LeadCard>
  );
}
